import {
  AdminCreateDocusignTemplate,
  AdminDeleteDocusignTemplate,
  AdminFetchDocusignTemplates,
} from 'src/constants/actionTypes'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { DocusignTemplateState } from './types'

const getInitialState = (): DocusignTemplateState => {
  return {
    templates: [],
    templatesLoading: false,
    templatesError: null,
    saving: false,
    deletingId: null,
  }
}

export const docusignTemplateReducer = (
  state: DocusignTemplateState = getInitialState(),
  action: Action<unknown>,
): DocusignTemplateState => {
  switch (action.type) {
    case AdminFetchDocusignTemplates.type.REQUEST:
      return {
        ...state,
        templates: [],
        templatesLoading: true,
        templatesError: null,
      }
    case AdminFetchDocusignTemplates.type.SUCCESS: {
      const templates = (action.payload as DocusignTemplate[] | undefined) ?? []

      return {
        ...state,
        templates,
        templatesLoading: false,
        templatesError: null,
      }
    }
    case AdminFetchDocusignTemplates.type.FAILED:
      return {
        ...state,
        templatesLoading: false,
        templatesError: resolveErrorMessage(action.payload, 'Failed to load DocuSign templates'),
      }

    // Create never writes to `templates` — the list is refetched on success, which is the single
    // source of truth. See the design doc's §6.4.
    case AdminCreateDocusignTemplate.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminCreateDocusignTemplate.type.SUCCESS:
    case AdminCreateDocusignTemplate.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    case AdminDeleteDocusignTemplate.type.REQUEST: {
      const payload = action.payload as { templateId?: string } | undefined

      return {
        ...state,
        deletingId: payload?.templateId ?? null,
      }
    }
    // Cleared on FAILED as well as SUCCESS: a foreign-key rejection from a template that an
    // insurance policy product still uses must not leave the row's Delete button spinning
    // forever. Deleting never removes the row here either — the refetch does that.
    case AdminDeleteDocusignTemplate.type.SUCCESS:
    case AdminDeleteDocusignTemplate.type.FAILED:
      return {
        ...state,
        deletingId: null,
      }

    default:
      return state
  }
}
